/**
 * features/quotations/QuotationFormPage.tsx — สร้าง/แก้ไขใบเสนอราคา
 *
 * โครงฟอร์ม 2 คอลัมน์: ซ้ายกรอกข้อมูล+รายการ, ขวาสรุปยอด (sticky) ตามที่ตกลง UX ไว้
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { quotationInputSchema, type QuotationInput } from '@ft/core';
import { ApiError } from '~/lib/api';
import { useCompany } from '~/features/company/hooks';
import { PageHeader } from '~/ui/layout';
import { Alert, Button, Field, Input, Textarea } from '~/ui/primitives';
import { useToast } from '~/ui/overlays';
import { CustomerPicker } from '~/features/documents/CustomerPicker';
import { DocumentItemsEditor } from '~/features/documents/DocumentItemsEditor';
import { DocumentSummaryPanel } from '~/features/documents/DocumentSummaryPanel';
import { EMPTY_DOCUMENT_FORM, type DocumentFormState } from '~/features/documents/types';
import { useCreateQuotation, useQuotation, useUpdateQuotation } from './hooks';

export function QuotationFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id) && id !== 'new';
  const navigate = useNavigate();
  const toast = useToast();

  const { data: existing, isLoading: loadingExisting } = useQuotation(isEdit ? id : undefined);
  const { data: company } = useCompany();

  const [form, setForm] = useState<DocumentFormState>(EMPTY_DOCUMENT_FORM);
  const [validUntil, setValidUntil] = useState('');
  const [itemErrors, setItemErrors] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const create = useCreateQuotation();
  const update = useUpdateQuotation();
  const saving = create.isPending || update.isPending;

  useEffect(() => {
    if (!existing) return;
    setForm({
      date: existing.date ?? new Date().toISOString().slice(0, 10),
      customerId: existing.customerId,
      customerName: existing.customerName,
      customerAddress: existing.customerAddress,
      customerTaxId: existing.customerTaxId,
      customerBranch: existing.customerBranch,
      customerPhone: existing.customerPhone,
      discountAmount: existing.discountAmount,
      notes: existing.notes,
      items: existing.items.map((item) => ({
        productName: item.productName,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unitPrice: item.unitPrice,
        discount: item.discount,
      })),
    });
    setValidUntil(existing.validUntil ?? '');
  }, [existing]);

  // ใบที่ออกใบกำกับภาษีแล้วห้ามแก้ไข — เด้งกลับไปหน้ารายละเอียดแทน
  useEffect(() => {
    if (isEdit && existing && !existing.editable) {
      toast.error('ใบเสนอราคานี้ออกใบกำกับภาษีไปแล้ว ไม่สามารถแก้ไขได้');
      navigate(`/quotations/${id}`, { replace: true });
    }
  }, [isEdit, existing, id, navigate, toast]);

  function patchForm(patch: Partial<DocumentFormState>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  async function handleSubmit() {
    setFormError(null);
    setItemErrors([]);

    const input: QuotationInput = {
      date: new Date(form.date),
      customerId: form.customerId,
      customerName: form.customerName,
      customerAddress: form.customerAddress,
      customerTaxId: form.customerTaxId,
      customerBranch: form.customerBranch,
      customerPhone: form.customerPhone,
      discountAmount: form.discountAmount,
      notes: form.notes,
      validUntil: validUntil ? new Date(validUntil) : null,
      status: existing?.status ?? 'draft',
      items: form.items,
    };

    const parsed = quotationInputSchema.safeParse(input);
    if (!parsed.success) {
      const itemIssues: string[] = [];
      let topError: string | null = null;

      for (const issue of parsed.error.issues) {
        if (issue.path[0] === 'items' && typeof issue.path[1] === 'number') {
          itemIssues[issue.path[1]] = issue.message;
        } else if (!topError) {
          topError = issue.message;
        }
      }

      setItemErrors(itemIssues);
      setFormError(topError ?? 'กรุณาตรวจสอบข้อมูลในฟอร์ม');
      return;
    }

    try {
      if (isEdit && id) {
        await update.mutateAsync({ id, input: parsed.data });
        toast.success('บันทึกใบเสนอราคาแล้ว');
        navigate(`/quotations/${id}`);
      } else {
        const created = await create.mutateAsync(parsed.data);
        toast.success(`สร้างใบเสนอราคา ${created.documentNumber} แล้ว`);
        navigate(`/quotations/${created.id}`);
      }
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : 'บันทึกไม่สำเร็จ กรุณาลองใหม่');
    }
  }

  if (isEdit && loadingExisting) {
    return <p className="text-sm text-fg-muted">กำลังโหลด…</p>;
  }

  return (
    <div>
      <PageHeader
        title={isEdit ? `แก้ไขใบเสนอราคา ${existing?.documentNumber ?? ''}` : 'สร้างใบเสนอราคา'}
        actions={
          <Button variant="ghost" onClick={() => navigate('/quotations')}>
            <ArrowLeft size={15} />
            กลับ
          </Button>
        }
      />

      {formError ? (
        <div className="mb-4">
          <Alert tone="danger">{formError}</Alert>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
        <div className="space-y-5">
          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-fg">ข้อมูลเอกสาร</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="วันที่เอกสาร" required>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(event) => patchForm({ date: event.target.value })}
                />
              </Field>
              <Field label="ยืนราคาถึง" hint="เว้นว่างเพื่อให้ระบบตั้งเป็น +15 วันอัตโนมัติ">
                <Input
                  type="date"
                  value={validUntil}
                  onChange={(event) => setValidUntil(event.target.value)}
                />
              </Field>
            </div>
          </div>

          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-fg">ข้อมูลลูกค้า</h3>
            <CustomerPicker form={form} onChange={patchForm} />
          </div>

          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-fg">รายการสินค้า/บริการ</h3>
            <DocumentItemsEditor
              items={form.items}
              onChange={(items) => patchForm({ items })}
              errors={itemErrors}
            />
          </div>

          <div className="card p-4">
            <Field label="หมายเหตุ">
              <Textarea
                value={form.notes}
                onChange={(event) => patchForm({ notes: event.target.value })}
                placeholder="เงื่อนไขการชำระเงิน, ข้อตกลงเพิ่มเติม ฯลฯ"
              />
            </Field>
          </div>
        </div>

        <DocumentSummaryPanel
          form={form}
          onDiscountChange={(value) => patchForm({ discountAmount: value })}
          vatEnabled={company?.vatEnabled ?? true}
          submitSlot={
            <Button variant="primary" onClick={() => void handleSubmit()} loading={saving} className="w-full">
              <Save size={15} />
              บันทึกใบเสนอราคา
            </Button>
          }
        />
      </div>
    </div>
  );
}
